import { create } from 'zustand';
import { apiService } from '../services/apiService';
import { jwtDecode } from 'jwt-decode';
import { ListAccountsRole } from '../../generated-src/client/account/api';
import { useBrandingStore } from './brandingStore';
import { optimizedApiService } from '../services/optimizedApiService';

// Локальный тип User (расширенный AccountResponse)
interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  language?: string;
  role: ListAccountsRole;
  teamId?: number | null;
  companyId?: number | null; // Добавляем companyId
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = ListAccountsRole | null;
export type AuthZone = 'crm';

type AuthUser = User | null;

type AuthStorageSource = 'local' | 'session';

type StoredAuthData = {
  token: string | null;
  userStr: string | null;
  role: UserRole;
  rememberMe: boolean;
};

interface AuthState {
  token: string | null;
  role: UserRole;
  user: AuthUser;
  isAuth: boolean;
  isLoading: boolean;
  error: string | null;
  showSessionExpiredModal: boolean;
  loginAdmin: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  restoreSession: () => Promise<void>;
  showSessionExpired: () => void;
  hideSessionExpired: () => void;
  setAuthError: (msg: string) => void;
  setUser: (user: User) => void;
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    return jwtDecode(token) as Record<string, unknown>;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return {};
  }
}

// Helper function to get storage key with zone
const getKey = (base: string, zone: AuthZone) => `${zone}_${base}`;

const emptyAuthData = () => ({
  token: null,
  userStr: null,
  role: null as UserRole,
  rememberMe: false,
});

const readAuthDataFromStorage = (
  storage: Storage,
  source: AuthStorageSource,
  zone: AuthZone,
): StoredAuthData => ({
  token: storage.getItem(getKey('auth_token', zone)),
  userStr: storage.getItem(getKey('auth_user', zone)),
  role: storage.getItem(getKey('auth_role', zone)) as UserRole,
  rememberMe: source === 'local' && storage.getItem(getKey('auth_rememberMe', zone)) === 'true',
});

const clearAuthDataFromStorage = (storage: Storage, zone: AuthZone) => {
  storage.removeItem(getKey('auth_token', zone));
  storage.removeItem(getKey('auth_user', zone));
  storage.removeItem(getKey('auth_role', zone));
  storage.removeItem(getKey('auth_rememberMe', zone));
};

const isCompleteAuthData = (authData: StoredAuthData): boolean => Boolean(authData.token && authData.userStr);

const toAuthDataResult = (authData: StoredAuthData) => ({
  token: authData.token,
  userStr: authData.userStr,
  role: authData.role,
  rememberMe: authData.rememberMe,
});

// Helper function to save auth data to storage
const saveAuthData = (token: string, user: AuthUser, role: string, rememberMe: boolean = false, zone: AuthZone = 'crm') => {
  clearAuthData(zone);
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem(getKey('auth_token', zone), token);
  storage.setItem(getKey('auth_user', zone), JSON.stringify(user));
  storage.setItem(getKey('auth_role', zone), role);
  storage.setItem(getKey('auth_rememberMe', zone), rememberMe.toString());
};

// Helper function to get auth data from storage
const getAuthData = (zone: AuthZone = 'crm') => {
  const localAuth = readAuthDataFromStorage(localStorage, 'local', zone);
  const sessionAuth = readAuthDataFromStorage(sessionStorage, 'session', zone);
  const hasLocalAuth = isCompleteAuthData(localAuth);
  const hasSessionAuth = isCompleteAuthData(sessionAuth);

  if (hasLocalAuth && hasSessionAuth) {
    clearAuthDataFromStorage(localStorage, zone);
    return toAuthDataResult(sessionAuth);
  }

  if (hasLocalAuth) return toAuthDataResult(localAuth);
  if (hasSessionAuth) return toAuthDataResult(sessionAuth);
  return emptyAuthData();
};

// Helper function to clear auth data from both storages
const clearAuthData = (zone: AuthZone = 'crm') => {
  clearAuthDataFromStorage(localStorage, zone);
  clearAuthDataFromStorage(sessionStorage, zone);
  optimizedApiService.clearCache();
};

const resetVacancyStateForAuthUserChange = async () => {
  optimizedApiService.clearCache();

  try {
    const { useVacancyStore } = await import('./vacancyStore');
    useVacancyStore.getState().resetForAuthUserChange();
  } catch (error) {
    console.warn('Failed to reset vacancy state after auth user change:', error);
  }
};

/**
 * authStore (Zustand)
 * Хранит состояние авторизации, токен, пользователя, роль, ошибки и методы для логина/логаута/восстановления сессии.
 *
 * Sequence diagram (Mermaid) — процесс авторизации:
 *
 * ```mermaid
 * sequenceDiagram
 *   participant UI as LoginForm
 *   participant Store as useAuthStore
 *   participant API as AuthApi/CandidatesApi
 *   participant Storage as localStorage/sessionStorage
 *
 *   UI->>Store: loginAdmin(email, password)
 *   Store->>API: POST /login
 *   API-->>Store: { token, user }
 *   Store->>Storage: saveAuthData(token, user, role)
 *   Store-->>UI: set({ token, user, role, isAuth: true })
 *
 *   UI->>Store: loginCandidate(data)
 *   Store->>API: POST /auth-candidate
 *   API-->>Store: { token, candidate }
 *   Store->>Storage: saveAuthData(token, candidate, role)
 *   Store-->>UI: set({ token, user: candidate, role, isAuth: true })
 *
 *   UI->>Store: restoreSession()
 *   Store->>Storage: getAuthData()
 *   alt token найден и не истёк
 *     Store-->>UI: set({ token, user, role, isAuth: true })
 *   else нет токена или истёк
 *     Store-->>UI: set({ isAuth: false })
 *   end
 * ```
 *
 * Бизнес-логика:
 * - loginAdmin: логинит администратора, сохраняет токен и пользователя, обновляет состояние
 * - restoreSession: восстанавливает сессию из localStorage/sessionStorage, проверяет валидность токена
 * - logout: очищает все данные, сбрасывает состояние
 */
export const useAuthStore = create<AuthState>((set, get) => {
  // Инициализируем состояние сразу
  const initState = () => {
    // Восстанавливаем сессию из хранилища
    const zone: AuthZone = 'crm';
    const { token, userStr, role } = getAuthData(zone);
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as AuthUser;
        const payload = parseJwt(token);
        
        const currentTime = Date.now() / 1000;
        if ((payload.exp as number | undefined) && (payload.exp as number) < currentTime) {
          // Токен истек
          clearAuthData(zone);
          localStorage.removeItem('admin_remember_me');
          return { token: null, role: null, user: null, isAuth: false, isLoading: false, error: null };
        }
        
        // Сессия валидна
        return { token, user, role, isAuth: true, error: null, isLoading: false };
      } catch (error) {
        console.error('Error parsing stored session:', error);
        clearAuthData(zone);
        return { token: null, role: null, user: null, isAuth: false, isLoading: false, error: null };
      }
    } else {
      // Fallback: поддержка легаси-токена Relevanter (adminToken/adminUser)
      try {
        const legacyToken = localStorage.getItem('adminToken');
        const legacyUserStr = localStorage.getItem('adminUser');
        if (legacyToken && legacyUserStr) {
          const legacyUser = JSON.parse(legacyUserStr) as AuthUser;
          // Сразу переносим в CRM-хранилище для единообразия
          saveAuthData(legacyToken, legacyUser, (ListAccountsRole.ADMIN as unknown as string), true, zone);
          return { token: legacyToken, user: legacyUser, role: ListAccountsRole.ADMIN, isAuth: true, isLoading: false, error: null };
        }
      } catch {}
      return { isAuth: false, isLoading: false };
    }
  };
  
  const initialState = initState();
  
  // Если нет токена при инициализации, запускаем асинхронную проверку
  if (!initialState.token) {
    // Устанавливаем isLoading в true для асинхронной проверки
    setTimeout(() => {
      get().restoreSession().catch(() => {
        // Игнорируем ошибки при восстановлении сессии
      });
    }, 0);
  }
  
  return {
  token: initialState.token || null,
  role: initialState.role || null,
  user: initialState.user || null,
  isAuth: initialState.isAuth || false,
  isLoading: initialState.isLoading !== undefined ? initialState.isLoading : (!initialState.token),
  error: initialState.error || null,
  showSessionExpiredModal: false,
  async loginAdmin(email: string, password: string, rememberMe: boolean = false) {
    try {
      const zone: AuthZone = 'crm';
      console.log('🔍 loginAdmin - Starting login process');
      const res = await apiService.getAuthenticatedClient().auth.login({ email, password, rememberMe });
      const { token, user } = res.data as { token: string; user: User };
      const payload = token ? parseJwt(token as string) : {};

      console.log('🔍 loginAdmin - User object from API:', user);
      console.log('🔍 loginAdmin - User companyId:', (user as any)?.companyId);
      console.log('🔍 loginAdmin - Token payload:', payload);

      const userRole = (user?.role as UserRole) || (payload.role as UserRole) || ListAccountsRole.ADMIN;
      
      if (token) {
        saveAuthData(token as string, user, userRole as string, rememberMe, zone);
        await resetVacancyStateForAuthUserChange();
      }

      set({ token, user, role: userRole, isAuth: true, error: null });
      apiService.refreshApiClient();

      // Очищаем старый брендинг перед загрузкой нового (важно при смене компании)
      useBrandingStore.getState().clearBranding();

      // Загружаем брендинг после успешного логина
      // ВАЖНО: ждём завершения, чтобы UI отрендерился с правильными цветами
      try {
        await useBrandingStore.getState().loadBranding();
        console.log('🔍 loginAdmin - Branding loaded successfully');
      } catch (error) {
        console.warn('🔍 Failed to load branding after login:', error);
        // Не блокируем логин при ошибке брендинга
      }

      console.log('🔍 loginAdmin - Login completed successfully');
    } catch (e: unknown) {
      const error = e instanceof Error ? e : { message: 'Ошибка авторизации' };
      console.error('🔍 loginAdmin - Login failed:', error);
      set({ error: (error as Error).message || 'Ошибка авторизации', isAuth: false });
      throw error;
    }
  },

  logout() {
    clearAuthData('crm');
    localStorage.removeItem('admin_remember_me');
    
    // Очищаем легаси-токены Relevanter
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    
    // Очищаем данные брендинга при выходе
    useBrandingStore.getState().clearBranding();
    void resetVacancyStateForAuthUserChange();
    
    set({ token: null, role: null, user: null, isAuth: false, isLoading: false, error: null });
    apiService.refreshApiClient();
  },
  async restoreSession() {
    set({ isLoading: true });
    try {
      const zone: AuthZone = 'crm';
      const { token, userStr, role } = getAuthData(zone);

      if (token && userStr) {
        const user = JSON.parse(userStr) as AuthUser;
        const payload = parseJwt(token);

        const currentTime = Date.now() / 1000;
        if ((payload.exp as number | undefined) && (payload.exp as number) < currentTime) {
          clearAuthData(zone);
          localStorage.removeItem('admin_remember_me');
          set({ token: null, role: null, user: null, isAuth: false, isLoading: false, error: null });
          apiService.refreshApiClient();
          return;
        }

        set({ token, user, role, isAuth: true, error: null, isLoading: false });
        apiService.refreshApiClient();

        // Инициализируем брендинг из localStorage при восстановлении сессии (для быстрого отображения)
        useBrandingStore.getState().initFromStorage();

        // Загружаем актуальный брендинг через API
        // Не ждём - initFromStorage уже загрузил кэшированные данные
        useBrandingStore.getState().loadBranding().catch(error => {
          console.warn('Failed to load branding after session restore:', error);
        });
      } else {
        set({ isAuth: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      clearAuthData('crm');
      localStorage.removeItem('admin_remember_me');
      set({ token: null, role: null, user: null, isAuth: false, isLoading: false, error: null });
      apiService.refreshApiClient();
    }
  },
  showSessionExpired() {
    set({ showSessionExpiredModal: true });
  },
  hideSessionExpired() {
    set({ showSessionExpiredModal: false });
  },
  setAuthError(msg: string) {
    set({ error: msg });
  },
  setUser(user: User) {
    set({ user });
    // Обновляем пользователя в localStorage/sessionStorage
    const zone: AuthZone = 'crm';
    const { token, role, rememberMe } = getAuthData(zone);
    if (token && role) {
      saveAuthData(token, user, role, rememberMe, zone);
    }
  },
  };
}); 
